import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BrowserRouter, Route, Routes, useNavigate } from "react-router-dom";
import { Download, List, RefreshCw } from "lucide-react";
import { Toaster, toast } from "sonner";

import "@/App.css";
import AdminUsersPanel from "@/components/AdminUsersPanel";
import AppSidebar from "@/components/AppSidebar";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import ArrAutomationPanel from "@/components/ArrAutomationPanel";
import BoardReportsPage from "@/components/BoardReportsPage";
import ChurnReportSection from "@/components/ChurnReportSection";
import ClientManagementPanel from "@/components/ClientManagementPanel";
import ConnectionPanel from "@/components/ConnectionPanel";
import CompanyDrilldownSheet from "@/components/CompanyDrilldownSheet";
import DrilldownSheet from "@/components/DrilldownSheet";
import FilterBar, { buildFyDates } from "@/components/FilterBar";
import KpiGrid from "@/components/KpiGrid";
import LoginPage from "@/components/LoginPage";
import MetricsConfigPanel, { BUILT_IN_KPIS } from "@/components/MetricsConfigPanel";
import ReportChart from "@/components/ReportChart";
import ReportTable from "@/components/ReportTable";
import SectionShell from "@/components/SectionShell";
import SettingsWorkspacePanel from "@/components/SettingsWorkspacePanel";
import StripeCancelPage from "@/components/StripeCancelPage";
import StripeCheckoutPage from "@/components/StripeCheckoutPage";
import StripeSuccessPage from "@/components/StripeSuccessPage";
import SubscriptionWall from "@/components/SubscriptionWall";
import { PALETTE_OPTIONS, DEFAULT_CUSTOM_COLORS } from "@/components/SettingsPanel";
import WorkspaceHome from "@/components/WorkspaceHome";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  exportIncomeStatement,
  exportRevenueByClientProduct,
  fetchArrDeals,
  fetchConnection,
  fetchConnections,
  fetchFilterOptions,
  fetchInstallUrl,
  fetchMe,
  fetchPasswordRequests,
  fetchMonthDetails,
  fetchWindowDetails,
  fetchPortalKpis,
  fetchPresets,
  fetchServerPortalSettings,
  createPreset,
  deletePreset,
  fetchChurnReport,
  fetchReport,
  fetchRevenueByClient,
  loadDealProperties,
  previewAnnualArr,
  recalculateAnnualArr,
  saveServerPortalSettings,
  startSync,
} from "@/lib/api";

// Seed the default filter window to the current fiscal year (July start by default).
// This is recomputed at page load so it always reflects the current year.
const _defaultFy = buildFyDates(7, 0);

const DEFAULT_FILTERS = {
  fromMonth: _defaultFy.from,
  toMonth: _defaultFy.to,
  stages: [],
  excludeStages: [],
  pipelines: [],
  owners: [],
  companies: [],
  products: [],
  billingFrequencies: [],
  probabilityMin: "",
  probabilityMax: "",
  closeDateFrom: "",
  closeDateTo: "",
  includeOneTime: false,
  collapseProducts: false,
  excludeNonXero: false,
  dynamicFilters: {},
  lineItemFilters: {},
};

const DEFAULT_SETTINGS = {
  title: "Monthly recurring revenue by product",
  subtitle: "Click any month to inspect the contributing HubSpot deals and line items.",
  arrTitle: "ARR Report",
  arrSubtitle: "Total recurring revenue across the selected period, broken down by product.",
  revenueByClientTitle: "Revenue by Client",
  revenueByClientSubtitle: "Per-company ARR across the selected period. Click a row to inspect contributing deals.",
  currency: "GBP",
  decimalPlaces: 0,
  roundWholeNumbers: true,
  showTotals: true,
  showLegend: true,
  dateFormat: "short",
  palette: "atlas",
  customColors: DEFAULT_CUSTOM_COLORS,
  visibleKpis: [],
  customMetrics: [],
  fiscalYearStartMonth: 7,   // 1=Jan … 12=Dec  (7 = July, most common UK FY start)
};

// Per-portal settings persistence — stored in localStorage so changes survive refresh.
const portalSettingsKey = (portalId) => `portaliq-settings-${portalId}`;

function loadPortalSettings(portalId, portalCurrency) {
  try {
    const raw = localStorage.getItem(portalSettingsKey(portalId));
    if (raw) {
      const saved = JSON.parse(raw);
      // If the portal currency is now known AND the saved value is the generic
      // placeholder default (never explicitly set by the user), upgrade it.
      // This handles the case where the portal synced before currency was detected.
      if (portalCurrency && (!saved.currency || saved.currency === "USD" || saved.currency === DEFAULT_SETTINGS.currency)) {
        saved.currency = portalCurrency;
      }
      // Strip legacy built-in KPI keys — only custom metric IDs stay in visibleKpis.
      if (Array.isArray(saved.visibleKpis)) {
        const customIds = new Set((saved.customMetrics || []).map((m) => m.id));
        saved.visibleKpis = saved.visibleKpis.filter((k) => customIds.has(k));
      }
      return { ...DEFAULT_SETTINGS, ...saved };
    }
  } catch {
    // corrupt entry — fall through to defaults
  }
  // First visit for this portal: seed currency from the HubSpot account.
  // Leave currency empty if the portal currency isn't known yet so the UI
  // prompts the user rather than silently assuming USD.
  return { ...DEFAULT_SETTINGS, currency: portalCurrency || "" };
}

function savePortalSettings(portalId, nextSettings) {
  try {
    localStorage.setItem(portalSettingsKey(portalId), JSON.stringify(nextSettings));
  } catch {
    // storage quota exceeded — ignore
  }
}

const EMPTY_FILTER_OPTIONS = {
  stages: [],
  pipelines: [],
  stages_by_pipeline: {},
  owners: [],
  companies: [],
  products: [],
  billing_frequencies: [],
  probability_range: { min: 0, max: 100 },
  available_properties: [],
};

function AppShell({ authEmail, onLogout }) {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState("user"); // "admin" | "user" | "client"
  // Change password: opened from the sidebar, or forced when an admin has set
  // a temporary password for this account.
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  // Admins see how many people are waiting for help with their password.
  const [passwordRequestCount, setPasswordRequestCount] = useState(0);
  const refreshPasswordRequests = useCallback(async () => {
    try {
      const result = await fetchPasswordRequests();
      setPasswordRequestCount(result.total || 0);
    } catch {
      /* not an admin, or offline: no badge */
    }
  }, []);
  useEffect(() => {
    if (userRole !== "admin") return undefined;
    refreshPasswordRequests();
    const timer = setInterval(refreshPasswordRequests, 60000);
    return () => clearInterval(timer);
  }, [userRole, refreshPasswordRequests]);
  const userRoleRef = useRef("user"); // ref copy so closures always see current value
  const [subscriptionStatus, setSubscriptionStatus] = useState(null); // "active" | "pending" | "canceled" | null
  const [subscriptionPlan, setSubscriptionPlan] = useState(null);
  const requireSubscription = process.env.REACT_APP_REQUIRE_SUBSCRIPTION === "true";
  const [installInfo, setInstallInfo] = useState(null);
  const [connections, setConnections] = useState([]);
  const [activePortalId, setActivePortalId] = useState("");
  const [activeConnection, setActiveConnection] = useState(null);
  // Track which portals have had their settings migrated from localStorage → server
  const migratedPortals = useRef(new Set());
  const [filterOptions, setFilterOptions] = useState(EMPTY_FILTER_OPTIONS);
  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [report, setReport] = useState(null);
  const [viewMode, setViewMode] = useState("stacked");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [drilldownOpen, setDrilldownOpen] = useState(false);
  const [monthDetail, setMonthDetail] = useState(null);
  const [detailMonth, setDetailMonth] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [manualArrDealId, setManualArrDealId] = useState("");
  const [manualArrPreviewLoading, setManualArrPreviewLoading] = useState(false);
  const [manualArrWriteLoading, setManualArrWriteLoading] = useState(false);
  const [manualArrPreview, setManualArrPreview] = useState(null);
  const [manualArrResult, setManualArrResult] = useState(null);
  const [presets, setPresets] = useState([]);
  const [activePropertyKeys, setActivePropertyKeys] = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("portaliq-sidebar-collapsed") === "true"
  );
  const [metricsConfigOpen, setMetricsConfigOpen] = useState(false);
  const [independentKpis, setIndependentKpis] = useState({});
  const [overviewKpis, setOverviewKpis] = useState(null);
  const [revenueByClient, setRevenueByClient] = useState(null);
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [churnReport, setChurnReport] = useState(null);
  const [churnLoading, setChurnLoading] = useState(false);
  const [churnDraftFilters, setChurnDraftFilters] = useState(DEFAULT_FILTERS);
  const [churnAppliedFilters, setChurnAppliedFilters] = useState(DEFAULT_FILTERS);
  const [churnActivePropertyKeys, setChurnActivePropertyKeys] = useState([]);
  const [arrDrillProduct, setArrDrillProduct] = useState(null);
  const [arrDrillDeals, setArrDrillDeals] = useState(null);
  const [arrDrillLoading, setArrDrillLoading] = useState(false);
  const [arrExpandedDeals, setArrExpandedDeals] = useState(new Set());

  const [companyDrilldownOpen, setCompanyDrilldownOpen] = useState(false);
  const [companyDrilldownCompany, setCompanyDrilldownCompany] = useState(null);
  const [companyDrilldownDetail, setCompanyDrilldownDetail] = useState(null);
  const [companyDrilldownLoading, setCompanyDrilldownLoading] = useState(false);
  const [companyDrilldownMonth, setCompanyDrilldownMonth] = useState("");
  const [companyDrilldownLocked, setCompanyDrilldownLocked] = useState(true);

  const handleToggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("portaliq-sidebar-collapsed", String(next));
      return next;
    });
  };

  const palette = useMemo(() => {
    if (settings.customColors && settings.customColors.length > 0) return settings.customColors;
    return PALETTE_OPTIONS.find((item) => item.id === settings.palette)?.colors || PALETTE_OPTIONS[0].colors;
  }, [settings.palette, settings.customColors]);

  const formatCurrency = (value, compact = false) => {
    const numberValue = Number(value || 0);
    const fractionDigits = settings.roundWholeNumbers ? 0 : Number(settings.decimalPlaces || 0);
    const currency = settings.currency || "USD";
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        maximumFractionDigits: fractionDigits,
        minimumFractionDigits: fractionDigits,
        notation: compact ? "compact" : "standard",
      }).format(numberValue);
    } catch {
      // Unknown currency code — format as plain number with code prefix.
      return `${currency} ${Intl.NumberFormat("en-US", { maximumFractionDigits: fractionDigits }).format(numberValue)}`;
    }
  };

  const normalizeMonthValue = (month) => {
    if (!month) {
      return "";
    }

    const value = String(month).trim();
    if (/^\d{4}-\d{2}$/.test(value)) {
      return value;
    }

    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return `${parsed.getFullYear()}-${`${parsed.getMonth() + 1}`.padStart(2, "0")}`;
    }

    return "";
  };

  const formatMonthLabel = (month) => {
    if (!month) {
      return "";
    }

    const normalizedMonth = normalizeMonthValue(month);
    if (!normalizedMonth) {
      return String(month);
    }

    const date = new Date(`${normalizedMonth}-01T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return String(month);
    }

    const formatMap = {
      short: { month: "short", year: "numeric" },
      compact: { month: "short", year: "2-digit" },
      long: { month: "long", year: "numeric" },
    };
    return new Intl.DateTimeFormat("en-US", formatMap[settings.dateFormat] || formatMap.short).format(date);
  };

  const hydrateConnections = async (preferredPortalId) => {
    const connectionList = await fetchConnections();
    setConnections(connectionList);
    const nextPortalId = preferredPortalId || localStorage.getItem("portaliq-active-portal") || connectionList[0]?.portal_id || "";
    if (nextPortalId) {
      setActivePortalId(nextPortalId);
      localStorage.setItem("portaliq-active-portal", nextPortalId);
    }
    return connectionList;
  };

  useEffect(() => {
    const bootstrap = async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const connectedPortal = searchParams.get("portalId") || "";
      const connected = searchParams.get("connected");
      const oauthError = searchParams.get("oauthError");

      // Fetch current user role — determines which portal(s) are visible
      let detectedRole = "admin";
      try {
        const me = await fetchMe();
        detectedRole = me.role || "admin";
        setUserRole(detectedRole);
        userRoleRef.current = detectedRole;
        setSubscriptionStatus(me.subscription_status || null);
        setSubscriptionPlan(me.subscription_plan || null);
        setMustChangePassword(Boolean(me.must_change_password));

        // If admin has no active subscription and subscription gating is on, redirect to checkout
        const isStripePage = window.location.pathname.startsWith("/stripe/");
        if (
          requireSubscription &&
          me.role !== "client" &&
          me.subscription_status !== "active" &&
          !isStripePage
        ) {
          navigate("/stripe/checkout", { replace: true });
          return;
        }

        // For clients with exactly one portal, auto-select it
        if (me.role === "client" && me.portal_access?.length === 1) {
          setActivePortalId(me.portal_access[0]);
          localStorage.setItem("portaliq-active-portal", me.portal_access[0]);
        }
      } catch {
        /* fallback to "admin" — non-critical */
      }

      try {
        const [installPayload, connectionList] = await Promise.all([fetchInstallUrl(), hydrateConnections(connectedPortal)]);
        setInstallInfo(installPayload);

        // Auto-navigate to reporting if the user lands on the home page and has a portal connected.
        // Clients always go straight to reporting; admins go there only from the root path.
        const atRoot = window.location.pathname === "/" || window.location.pathname === "";
        const hasPortal = connectionList.length > 0;
        if (hasPortal && (detectedRole === "client" || atRoot) && !connected && !oauthError) {
          navigate("/mrr-reporting", { replace: true });
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || "Unable to load HubSpot configuration.");
      }

      if (connected) {
        toast.success("HubSpot portal connected. Initial sync started.");
      }
      if (oauthError) {
        toast.error(decodeURIComponent(oauthError));
      }
      if (connected || oauthError) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };

    bootstrap();
  }, []);

  useEffect(() => {
    if (!activePortalId) {
      return undefined;
    }

    const loadPortal = async () => {
      try {
        const [connection, options, serverSettings] = await Promise.all([
          fetchConnection(activePortalId),
          fetchFilterOptions(activePortalId),
          fetchServerPortalSettings(activePortalId).catch(() => null),
        ]);
        setActiveConnection(connection);
        setFilterOptions(options);

        // Build settings: prefer server, fall back to localStorage for migration
        const hasServerSettings = serverSettings && Object.keys(serverSettings).some((k) => serverSettings[k] != null);
        if (hasServerSettings) {
          // Convert snake_case server keys → camelCase frontend keys
          const merged = {
            ...DEFAULT_SETTINGS,
            ...(serverSettings.title != null && { title: serverSettings.title }),
            ...(serverSettings.subtitle != null && { subtitle: serverSettings.subtitle }),
            ...(serverSettings.arr_title != null && { arrTitle: serverSettings.arr_title }),
            ...(serverSettings.arr_subtitle != null && { arrSubtitle: serverSettings.arr_subtitle }),
            ...(serverSettings.revenue_by_client_title != null && { revenueByClientTitle: serverSettings.revenue_by_client_title }),
            ...(serverSettings.revenue_by_client_subtitle != null && { revenueByClientSubtitle: serverSettings.revenue_by_client_subtitle }),
            ...(serverSettings.currency != null && { currency: serverSettings.currency }),
            ...(serverSettings.decimal_places != null && { decimalPlaces: serverSettings.decimal_places }),
            ...(serverSettings.round_whole_numbers != null && { roundWholeNumbers: serverSettings.round_whole_numbers }),
            ...(serverSettings.show_totals != null && { showTotals: serverSettings.show_totals }),
            ...(serverSettings.show_legend != null && { showLegend: serverSettings.show_legend }),
            ...(serverSettings.date_format != null && { dateFormat: serverSettings.date_format }),
            ...(serverSettings.palette != null && { palette: serverSettings.palette }),
            ...(serverSettings.custom_colors != null && { customColors: serverSettings.custom_colors }),
            ...(serverSettings.custom_metrics != null && { customMetrics: serverSettings.custom_metrics }),
            ...(serverSettings.fiscal_year_start_month != null && { fiscalYearStartMonth: serverSettings.fiscal_year_start_month }),
          };
          // Strip legacy built-in KPI keys from visibleKpis — keep only custom metric IDs.
          if (serverSettings.visible_kpis != null) {
            const customIds = new Set((merged.customMetrics || []).map((m) => m.id));
            merged.visibleKpis = serverSettings.visible_kpis.filter((k) => customIds.has(k));
          }
          // Apply portal currency override (same logic as before)
          if (connection?.portal_currency && (!merged.currency || merged.currency === "USD" || merged.currency === "GBP")) {
            merged.currency = connection.portal_currency;
          }
          setSettings(merged);
        } else {
          // No server settings yet — load from localStorage and migrate once
          const localSettings = loadPortalSettings(activePortalId, connection?.portal_currency);
          setSettings(localSettings);
          // Migrate localStorage → server (admin only, once per portal per session)
          if (userRoleRef.current !== "client" && !migratedPortals.current.has(activePortalId)) {
            migratedPortals.current.add(activePortalId);
            saveServerPortalSettings(activePortalId, localSettings).catch(() => {});
          }
        }

        const hasDealProps = (options.available_properties || []).some((p) => p.type === "deal_property");
        if (!hasDealProps && connection?.portal_id) {
          try {
            await loadDealProperties(connection.portal_id);
            const refreshed = await fetchFilterOptions(activePortalId);
            setFilterOptions(refreshed);
          } catch {
            /* property load is best-effort */
          }
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || "Unable to load portal details.");
      }
    };

    loadPortal();
    return undefined;
  }, [activePortalId]);

  useEffect(() => {
    if (!activePortalId) {
      return undefined;
    }

    const loadReport = async () => {
      try {
        setReportLoading(true);
        const response = await fetchReport(activePortalId, appliedFilters, settings.fiscalYearStartMonth);
        setReport(response);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Unable to load the MRR report.");
      } finally {
        setReportLoading(false);
      }
    };

    loadReport();
    return undefined;
  }, [activePortalId, appliedFilters]);

  // Re-fetch drill-down automatically when filters change while a product is open
  useEffect(() => {
    if (!activePortalId || !arrDrillProduct) return;
    let cancelled = false;
    setArrDrillLoading(true);
    fetchArrDeals(activePortalId, arrDrillProduct, appliedFilters)
      .then((data) => { if (!cancelled) setArrDrillDeals(data.deals || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setArrDrillLoading(false); });
    return () => { cancelled = true; };
  }, [activePortalId, appliedFilters, arrDrillProduct]);

  useEffect(() => {
    if (!activePortalId) return;
    const configs = new Map();
    for (const metric of settings.customMetrics || []) {
      // Simple metrics only have operand_a; formula metrics have operand_a + operand_b.
      const ops = metric.mode === "simple"
        ? [metric.operand_a]
        : [metric.operand_a, metric.operand_b];
      for (const op of ops) {
        if (op?.type === "independent_kpi") {
          const pipes = (op.pipelines || []).slice().sort();
          const stgs  = (op.stages   || []).slice().sort();
          const mode  = op.revenue_mode === "projected" ? "projected" : "actual";
          const key = [pipes.join(",") || "__all__", stgs.join(",") || "__all__", mode].join("|");
          if (!configs.has(key)) configs.set(key, { pipelines: op.pipelines || [], stages: op.stages || [], revenueMode: mode });
        }
      }
    }
    if (configs.size === 0) { setIndependentKpis({}); return; }

    Promise.all(
      [...configs.entries()].map(async ([key, cfg]) => {
        const kpis = await fetchPortalKpis(activePortalId, cfg, settings.fiscalYearStartMonth ?? 7);
        return [key, kpis];
      })
    ).then(results => {
      setIndependentKpis(Object.fromEntries(results));
    }).catch(() => {});
  }, [activePortalId, settings.customMetrics, settings.fiscalYearStartMonth]);

  // Load portal KPIs for the Reporting Overview section (independent of MRR report filters)
  useEffect(() => {
    if (!activePortalId) return;
    fetchPortalKpis(activePortalId, {}, settings.fiscalYearStartMonth ?? 7)
      .then((kpis) => setOverviewKpis(kpis))
      .catch(() => {});
  }, [activePortalId, settings.fiscalYearStartMonth]);

  // Load Revenue by Client whenever applied filters or portal change (shares same filters as MRR report)
  useEffect(() => {
    if (!activePortalId) return undefined;
    const load = async () => {
      try {
        setRevenueLoading(true);
        const data = await fetchRevenueByClient(activePortalId, appliedFilters);
        setRevenueByClient(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load Revenue by Client.");
      } finally {
        setRevenueLoading(false);
      }
    };
    load();
    return undefined;
  }, [activePortalId, appliedFilters]);

  // Load Churn Report whenever churn-specific filters or portal change
  useEffect(() => {
    if (!activePortalId) return undefined;
    const load = async () => {
      try {
        setChurnLoading(true);
        const data = await fetchChurnReport(activePortalId, churnAppliedFilters);
        setChurnReport(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load Churn Report.");
      } finally {
        setChurnLoading(false);
      }
    };
    load();
    return undefined;
  }, [activePortalId, churnAppliedFilters]);

  useEffect(() => {
    if (activeConnection?.sync_status !== "running" || !activePortalId) {
      return undefined;
    }

    const interval = window.setInterval(async () => {
      try {
        const connection = await fetchConnection(activePortalId);
        setActiveConnection(connection);
        setConnections((current) => current.map((item) => (item.portal_id === activePortalId ? connection : item)));
        if (connection.sync_status === "success") {
          const [options, nextReport] = await Promise.all([fetchFilterOptions(activePortalId), fetchReport(activePortalId, appliedFilters, settings.fiscalYearStartMonth)]);
          setFilterOptions(options);
          setReport(nextReport);
          toast.success("HubSpot data refreshed.");
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || "Unable to refresh sync status.");
      }
    }, 4000);

    return () => window.clearInterval(interval);
  }, [activeConnection?.sync_status, activePortalId, appliedFilters]);

  // Tick boxes that only change how the same rows are presented take effect
  // as soon as they are ticked. Everything else waits for Apply, but a switch
  // that visibly does nothing until a button is pressed reads as broken —
  // which is exactly how "Group products" read.
  const INSTANT_FILTERS = ["collapseProducts", "includeOneTime", "excludeNonXero"];

  const handleFilterChange = (key, value) => {
    setDraftFilters((current) => ({ ...current, [key]: value }));
    if (INSTANT_FILTERS.includes(key)) {
      setAppliedFilters((current) => ({ ...current, [key]: value }));
    }
  };

  const handleApplyFilters = () => {
    setAppliedFilters(draftFilters);
  };

  const handleResetFilters = () => {
    setDraftFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setActivePropertyKeys([]);
  };

  const handleAddProperty = (key) => {
    setActivePropertyKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
  };

  const handleRemoveProperty = (key) => {
    setActivePropertyKeys((prev) => prev.filter((k) => k !== key));
    const stdFields = ["stages", "pipelines", "owners", "companies", "products", "billingFrequencies"];
    if (stdFields.includes(key)) {
      setDraftFilters((cur) => ({ ...cur, [key]: [] }));
    } else if (key.startsWith("dp:")) {
      const propName = key.slice(3);
      setDraftFilters((cur) => {
        const next = { ...cur.dynamicFilters };
        delete next[propName];
        return { ...cur, dynamicFilters: next };
      });
    } else if (key.startsWith("li:")) {
      const propName = key.slice(3);
      setDraftFilters((cur) => {
        const next = { ...cur.lineItemFilters };
        delete next[propName];
        return { ...cur, lineItemFilters: next };
      });
    }
  };

  const handleToggleLineItem = (propName, value) => {
    setDraftFilters((cur) => {
      const current = cur.lineItemFilters?.[propName] || [];
      const has = current.includes(value);
      const next = has ? current.filter((v) => v !== value) : [...current, value];
      return { ...cur, lineItemFilters: { ...cur.lineItemFilters, [propName]: next } };
    });
  };

  const handleToggleDynamic = (propName, value) => {
    setDraftFilters((cur) => {
      const current = cur.dynamicFilters?.[propName] || [];
      const has = current.includes(value);
      const next = has ? current.filter((v) => v !== value) : [...current, value];
      return { ...cur, dynamicFilters: { ...cur.dynamicFilters, [propName]: next } };
    });
  };

  // ── Churn report filter handlers ────────────────────────────────────────
  const handleChurnFilterChange = (key, value) => {
    setChurnDraftFilters((current) => ({ ...current, [key]: value }));
  };

  const handleApplyChurnFilters = () => {
    setChurnAppliedFilters(churnDraftFilters);
  };

  const handleResetChurnFilters = () => {
    setChurnDraftFilters(DEFAULT_FILTERS);
    setChurnAppliedFilters(DEFAULT_FILTERS);
    setChurnActivePropertyKeys([]);
  };

  const handleAddChurnProperty = (key) => {
    setChurnActivePropertyKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
  };

  const handleRemoveChurnProperty = (key) => {
    setChurnActivePropertyKeys((prev) => prev.filter((k) => k !== key));
    const stdFields = ["stages", "pipelines", "owners", "companies", "products", "billingFrequencies"];
    if (stdFields.includes(key)) {
      setChurnDraftFilters((cur) => ({ ...cur, [key]: [] }));
    } else if (key.startsWith("dp:")) {
      const propName = key.slice(3);
      setChurnDraftFilters((cur) => {
        const next = { ...cur.dynamicFilters };
        delete next[propName];
        return { ...cur, dynamicFilters: next };
      });
    } else if (key.startsWith("li:")) {
      const propName = key.slice(3);
      setChurnDraftFilters((cur) => {
        const next = { ...cur.lineItemFilters };
        delete next[propName];
        return { ...cur, lineItemFilters: next };
      });
    }
  };

  const handleToggleChurnLineItem = (propName, value) => {
    setChurnDraftFilters((cur) => {
      const current = cur.lineItemFilters?.[propName] || [];
      const has = current.includes(value);
      const next = has ? current.filter((v) => v !== value) : [...current, value];
      return { ...cur, lineItemFilters: { ...cur.lineItemFilters, [propName]: next } };
    });
  };

  const handleToggleChurnDynamic = (propName, value) => {
    setChurnDraftFilters((cur) => {
      const current = cur.dynamicFilters?.[propName] || [];
      const has = current.includes(value);
      const next = has ? current.filter((v) => v !== value) : [...current, value];
      return { ...cur, dynamicFilters: { ...cur.dynamicFilters, [propName]: next } };
    });
  };

  const handleLoadChurnPreset = (preset) => {
    const f = preset.filters || {};
    setChurnDraftFilters({ ...DEFAULT_FILTERS, ...f });
    setChurnAppliedFilters({ ...DEFAULT_FILTERS, ...f });
    const keys = [];
    if (f.stages?.length) keys.push("stages");
    if (f.pipelines?.length) keys.push("pipelines");
    if (f.owners?.length) keys.push("owners");
    if (f.companies?.length) keys.push("companies");
    if (f.products?.length) keys.push("products");
    if (f.billingFrequencies?.length) keys.push("billingFrequencies");
    for (const k of Object.keys(f.dynamicFilters || {})) keys.push(`dp:${k}`);
    for (const k of Object.keys(f.lineItemFilters || {})) keys.push(`li:${k}`);
    setChurnActivePropertyKeys(keys);
  };

  const handleQuickSetChurnProbability = (min, max) => {
    setChurnDraftFilters((cur) => ({ ...cur, probabilityMin: min, probabilityMax: max }));
  };

  const handleSaveChurnPreset = async (name) => {
    if (!activePortalId) return;
    try {
      const created = await createPreset(activePortalId, name, churnAppliedFilters);
      setPresets((prev) => [...prev, created]);
      toast.success(`Preset "${name}" saved.`);
    } catch {
      toast.error("Could not save preset.");
    }
  };

  const handleRefreshProperties = async () => {
    if (!activePortalId) return;
    try {
      await loadDealProperties(activePortalId, true);
      const refreshed = await fetchFilterOptions(activePortalId);
      setFilterOptions(refreshed);
      toast.success("Deal properties reloaded.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load deal properties. You may need to reconnect your HubSpot portal to authorize the new scope.");
    }
  };

  const handlePortalSelect = (portalId) => {
    setActivePortalId(portalId);
    localStorage.setItem("portaliq-active-portal", portalId);
    setManualArrPreview(null);
    setManualArrResult(null);
  };

  const handleSync = async (portalId) => {
    try {
      const response = await startSync(portalId);
      toast.success(response.message);
      const connection = await fetchConnection(portalId);
      setActiveConnection(connection);
      setConnections((current) => current.map((item) => (item.portal_id === portalId ? connection : item)));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to start sync.");
    }
  };

  const handleMonthClick = async (month) => {
    const normalizedMonth = normalizeMonthValue(month);
    if (!activePortalId || !normalizedMonth) {
      return;
    }
    setDrilldownOpen(true);
    setDetailLoading(true);
    setDetailMonth(normalizedMonth);
    try {
      // MRR drilldown always shows recurring only — strip includeOneTime
      const { includeOneTime: _iot, ...mrrFilters } = appliedFilters;
      const detail = await fetchMonthDetails(activePortalId, normalizedMonth, mrrFilters);
      setMonthDetail(detail);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load month details.");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleViewAllLineItems = async () => {
    if (!activePortalId) return;
    setDrilldownOpen(true);
    setDetailLoading(true);
    setDetailMonth("window");
    try {
      const { includeOneTime: _iot, ...mrrFilters } = appliedFilters;
      const detail = await fetchWindowDetails(activePortalId, mrrFilters);
      setMonthDetail(detail);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load line items.");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleRefetchDetail = async (changesOnly) => {
    if (!activePortalId || !detailMonth || detailMonth === "window") return;
    setDetailLoading(true);
    try {
      const { includeOneTime: _iot2, ...mrrFilters2 } = appliedFilters;
      const detail = await fetchMonthDetails(activePortalId, detailMonth, mrrFilters2, changesOnly);
      setMonthDetail(detail);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to reload month details.");
    } finally {
      setDetailLoading(false);
    }
  };

  const currentMonthStr = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  };

  const fetchCompanyDetail = async (company, month, locked) => {
    if (!activePortalId) return;
    setCompanyDrilldownLoading(true);
    try {
      const filters = locked
        ? { ...appliedFilters, companies: [company] }
        : { ...appliedFilters, companies: [] };
      const detail = await fetchMonthDetails(activePortalId, month, filters);
      setCompanyDrilldownDetail(detail);
    } catch {
      setCompanyDrilldownDetail(null);
    } finally {
      setCompanyDrilldownLoading(false);
    }
  };

  const handleCompanyClick = (company) => {
    const month = currentMonthStr();
    setCompanyDrilldownCompany(company);
    setCompanyDrilldownMonth(month);
    setCompanyDrilldownLocked(true);
    setCompanyDrilldownDetail(null);
    setCompanyDrilldownOpen(true);
    fetchCompanyDetail(company, month, true);
  };

  const handleCompanyMonthChange = (month) => {
    setCompanyDrilldownMonth(month);
    fetchCompanyDetail(companyDrilldownCompany, month, companyDrilldownLocked);
  };

  const handleCompanyLockChange = (locked) => {
    setCompanyDrilldownLocked(locked);
    fetchCompanyDetail(companyDrilldownCompany, companyDrilldownMonth, locked);
  };

  const updateSetting = (key, value) => {
    setSettings((current) => {
      const next = { ...current, [key]: value };
      // Always save to localStorage for instant persistence
      if (activePortalId) savePortalSettings(activePortalId, next);
      // Admin: also sync to server so clients see the same settings
      if (activePortalId && userRoleRef.current !== "client") {
        saveServerPortalSettings(activePortalId, next).catch(() => {});
      }
      return next;
    });
  };

  const loadPresets = async () => {
    if (!activePortalId) return;
    try {
      const list = await fetchPresets(activePortalId);
      setPresets(list);
    } catch {
      /* silent */
    }
  };

  useEffect(() => {
    if (activePortalId) loadPresets();
  }, [activePortalId]);

  const handleSavePreset = async (name) => {
    if (!activePortalId || !name.trim()) return;
    try {
      await createPreset(activePortalId, name.trim(), { ...appliedFilters, activePropertyKeys });
      toast.success(`Preset "${name.trim()}" saved.`);
      await loadPresets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save preset.");
    }
  };

  const handleLoadPreset = (preset) => {
    const f = preset.filters || {};
    const restored = {
      fromMonth: f.fromMonth || "",
      toMonth: f.toMonth || "",
      stages: f.stages || [],
      excludeStages: f.excludeStages || [],
      pipelines: f.pipelines || [],
      owners: f.owners || [],
      companies: f.companies || [],
      products: f.products || [],
      billingFrequencies: f.billingFrequencies || [],
      probabilityMin: f.probabilityMin ?? "",
      probabilityMax: f.probabilityMax ?? "",
      closeDateFrom: f.closeDateFrom || "",
      closeDateTo: f.closeDateTo || "",
      includeOneTime: f.includeOneTime || false,
      collapseProducts: f.collapseProducts || false,
      excludeNonXero: f.excludeNonXero || false,
      dynamicFilters: f.dynamicFilters || {},
      lineItemFilters: f.lineItemFilters || {},
    };
    setDraftFilters(restored);
    setAppliedFilters(restored);
    setActivePropertyKeys(f.activePropertyKeys || []);
    toast.success(`Loaded preset "${preset.name}".`);
  };

  const handleDeletePreset = async (presetId) => {
    try {
      await deletePreset(presetId);
      toast.success("Preset deleted.");
      await loadPresets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to delete preset.");
    }
  };

  const handleExportCsv = () => {
    if (!report || !report.table?.length) {
      toast.error("No report data to export.");
      return;
    }
    const products = report.products || [];
    const headers = ["Month", "Total MRR", ...products];
    const rows = report.table.map((row) => [
      row.label || row.month,
      row.total_mrr,
      ...products.map((p) => row.product_values?.[p] || 0),
    ]);
    const csvContent = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `mrr-report-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported.");
  };

  const handleQuickSetProbability = (min, max = "") => {
    const next = { ...draftFilters, probabilityMin: String(min), probabilityMax: String(max) };
    setDraftFilters(next);
    setAppliedFilters(next);
  };

  const handleExportExcel = async () => {
    if (!activePortalId) {
      toast.error("No portal selected.");
      return;
    }
    try {
      const blob = await exportIncomeStatement(activePortalId, appliedFilters, settings.fiscalYearStartMonth);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `income-statement-${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Income statement exported.");
    } catch {
      toast.error("Could not export income statement.");
    }
  };

  const handleExportClientRevenue = async () => {
    if (!activePortalId) {
      toast.error("No portal selected.");
      return;
    }
    try {
      const blob = await exportRevenueByClientProduct(activePortalId, appliedFilters);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const from = appliedFilters.fromMonth || "start";
      const to = appliedFilters.toMonth || "present";
      link.download = `portaliq-revenue-${from}-to-${to}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Client revenue exported.");
    } catch {
      toast.error("Could not export client revenue.");
    }
  };

  const handleArrProductClick = async (product) => {
    if (arrDrillProduct === product) {
      setArrDrillProduct(null);
      setArrDrillDeals(null);
      return;
    }
    setArrDrillProduct(product);
    setArrDrillDeals(null);
    setArrDrillLoading(true);
    try {
      const data = await fetchArrDeals(activePortalId, product, appliedFilters);
      setArrDrillDeals(data.deals || []);
    } catch {
      toast.error("Could not load deals for this product.");
      setArrDrillProduct(null);
    } finally {
      setArrDrillLoading(false);
    }
  };

  const handleManualArrPreview = async () => {
    if (!activePortalId || !manualArrDealId.trim()) {
      toast.error("Please select a portal and enter a HubSpot deal ID.");
      return;
    }
    try {
      setManualArrPreviewLoading(true);
      const preview = await previewAnnualArr(activePortalId, manualArrDealId.trim());
      setManualArrPreview(preview);
      setManualArrResult(null);
      toast.success(`Preview ready for deal ${preview.deal_id}.`);
    } catch (error) {
      setManualArrPreview(null);
      setManualArrResult(null);
      toast.error(error.response?.data?.detail || "Unable to preview ARR for that deal.");
    } finally {
      setManualArrPreviewLoading(false);
    }
  };

  const handleManualArrWrite = async () => {
    if (!activePortalId || !manualArrPreview || !manualArrDealId.trim()) {
      toast.error("Generate a preview before writing ARR back to HubSpot.");
      return;
    }

    const confirmed = window.confirm(
      `Write ${manualArrPreview.annual_recurring_revenue} to annual_recurring_revenue for deal ${manualArrDealId.trim()} and archive the previous value to arr_last_year?`
    );
    if (!confirmed) {
      return;
    }

    try {
      setManualArrWriteLoading(true);
      const result = await recalculateAnnualArr(activePortalId, manualArrDealId.trim());
      setManualArrResult(result);
      toast.success(`ARR updated to ${formatCurrency(result.annual_recurring_revenue)} for deal ${result.deal_id}.`);
    } catch (error) {
      setManualArrResult(null);
      toast.error(error.response?.data?.detail || "Unable to write ARR for that deal.");
    } finally {
      setManualArrWriteLoading(false);
    }
  };

  const reportSubtitle = activeConnection
    ? `${settings.subtitle} Active portal: ${activeConnection.portal_name || activeConnection.portal_id}.`
    : settings.subtitle;

  const summaryItems = [
    {
      label: "Connected portal",
      value: activeConnection?.portal_name || "None yet",
      caption: "Current workspace source",
      testId: "workspace-summary-portal",
    },
    {
      label: "Last sync",
      value: activeConnection?.last_sync_at ? new Date(activeConnection.last_sync_at).toLocaleDateString() : "Pending",
      caption: "Latest successful sync",
      testId: "workspace-summary-sync",
    },
    {
      label: "Synced line items",
      value: String(activeConnection?.synced_line_items ?? 0),
      caption: "Recurring line items available",
      testId: "workspace-summary-line-items",
    },
  ];

  const homeCards = [
    {
      title: "MRR Reporting",
      description: "Open the reporting workspace to filter recurring revenue, inspect trends, and drill into contributing line items.",
      href: "/mrr-reporting",
      icon: "mrr",
      span: "md:col-span-5 lg:col-span-6",
      testId: "workspace-card-mrr",
    },
    {
      title: "ARR Sync",
      description: "Manage the annual ARR writeback trigger, review the webhook setup, and run manual ARR recalculations.",
      href: "/arr-sync",
      icon: "arr",
      span: "md:col-span-3 lg:col-span-6",
      testId: "workspace-card-arr",
    },
    {
      title: "HubSpot Connection",
      description: "Connect portals, refresh data, confirm OAuth setup, and review current connection health.",
      href: "/connections",
      icon: "connection",
      span: "md:col-span-4 lg:col-span-7",
      testId: "workspace-card-connection",
    },
    {
      title: "Settings",
      description: "Adjust chart naming, date formatting, rounding, palette choices, and reporting defaults.",
      href: "/settings",
      icon: "settings",
      span: "md:col-span-4 lg:col-span-5",
      testId: "workspace-card-settings",
    },
  ];

  const sharedRefreshButton = activeConnection ? (
    <Button
      variant="outline"
      className="h-10 rounded-full border-[#dddddd] bg-white px-4 text-[#323c44] hover:bg-[#efefef]"
      onClick={() => handleSync(activeConnection.portal_id)}
      data-testid="section-refresh-button"
    >
      <RefreshCw className={`h-4 w-4 ${activeConnection.sync_status === "running" ? "animate-spin" : ""}`} />
      Refresh data
    </Button>
  ) : null;

  return (
    <div className="App flex h-screen overflow-hidden bg-[#efefef] text-[#323c44]">
      <ChangePasswordDialog
        open={changePasswordOpen || mustChangePassword}
        forced={mustChangePassword}
        onClose={() => setChangePasswordOpen(false)}
        onChanged={() => {
          // Keep the dialog up to show it worked, now closable.
          setMustChangePassword(false);
          setChangePasswordOpen(true);
        }}
      />
      <AppSidebar
        authEmail={authEmail}
        onLogout={onLogout}
        onChangePassword={() => setChangePasswordOpen(true)}
        passwordRequestCount={passwordRequestCount}
        activeConnection={activeConnection}
        collapsed={sidebarCollapsed}
        onToggle={handleToggleSidebar}
        userRole={userRole}
      />
      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          <Routes>
        {/* ── Platform admin ── */}
        {userRole === "admin" && (
          <Route
            path="/admin/users"
            element={
              <SectionShell title="Platform Users" description="Monitor all registered accounts, subscriptions and portal access.">
                <AdminUsersPanel onPasswordRequestsChange={refreshPasswordRequests} />
              </SectionShell>
            }
          />
        )}
        {/* ── Stripe pages (full-screen, no sidebar content) ── */}
        <Route path="/stripe/checkout" element={<StripeCheckoutPage />} />
        <Route path="/stripe/success" element={<StripeSuccessPage />} />
        <Route path="/stripe/cancel" element={<StripeCancelPage />} />
        <Route path="/" element={<WorkspaceHome summaryItems={summaryItems} cards={homeCards} activeConnection={activeConnection} />} />
        <Route
          path="/connections"
          element={
            <SectionShell title="HubSpot Connection" description="Manage portal connections, refresh synced data, and confirm the OAuth setup." action={sharedRefreshButton}>
              <ConnectionPanel
                installInfo={installInfo}
                connections={connections}
                activePortalId={activePortalId}
                onSelectPortal={handlePortalSelect}
                onSync={handleSync}
                userRole={userRole}
              />
            </SectionShell>
          }
        />
        {/* ── Board reports: the pack, the builder and the reconciliation
             view. Separate from Reporting, which keeps the revenue reports
             exactly as they were. ── */}
        <Route
          path="/board-reports"
          element={
            <SectionShell
              title="Board Reports"
              description="The board pack, the report builder behind it, and the reconciliation against HubSpot."
            >
              <BoardReportsPage
                portalId={activePortalId}
                portalName={activeConnection?.portal_name}
                fyStartMonth={settings.fiscalYearStartMonth ?? 7}
              />
            </SectionShell>
          }
        />
        <Route
          path="/arr-sync"
          element={
            <SectionShell title="ARR Sync" description="Configure and manually trigger annual ARR writeback without mixing it into the reporting workspace." action={sharedRefreshButton}>
              <ArrAutomationPanel
                activePortalId={activePortalId}
                manualDealId={manualArrDealId}
                onManualDealIdChange={(value) => {
                  setManualArrDealId(value);
                  setManualArrPreview(null);
                  setManualArrResult(null);
                }}
                onManualArrPreview={handleManualArrPreview}
                onManualArrWrite={handleManualArrWrite}
                manualArrPreviewLoading={manualArrPreviewLoading}
                manualArrWriteLoading={manualArrWriteLoading}
                manualArrPreview={manualArrPreview}
                manualArrResult={manualArrResult}
              />
            </SectionShell>
          }
        />
        <Route
          path="/settings"
          element={
            <SectionShell title="Settings" description="Keep reporting presentation controls in a dedicated place so the reporting and sync pages stay focused.">
              <div className="space-y-8">
                <SettingsWorkspacePanel settings={settings} onUpdate={updateSetting} />
                {userRole !== "client" && activePortalId && (
                  <div className="rounded-xl border border-white/10 bg-[#323c44] p-6">
                    <ClientManagementPanel
                      portalId={activePortalId}
                      portalName={activeConnection?.portal_name}
                    />
                  </div>
                )}
              </div>
            </SectionShell>
          }
        />
        <Route
          path="/mrr-reporting"
          element={
            <SectionShell title="Reporting" description="Revenue intelligence — overview metrics, MRR trend reporting, and revenue by client." action={sharedRefreshButton}>
              {activePortalId ? (
                <div className="space-y-8" data-testid="mrr-reporting-page">

                  {/* ── Section 1: Filters ────────────────────────────────────── */}
                  <section data-testid="global-filters-section">
                    <div className="mb-4">
                      <h2 className="font-sans text-xl font-bold text-[#323c44]">Filters</h2>
                      <p className="mt-1 text-sm text-[#4a5b68]">Select filters once — they apply to ARR, MRR and Revenue by Client below.</p>
                    </div>
                    <FilterBar
                      filterOptions={filterOptions}
                      filters={draftFilters}
                      onChange={handleFilterChange}
                      onApply={handleApplyFilters}
                      onReset={handleResetFilters}
                      activePropertyKeys={activePropertyKeys}
                      onAddProperty={handleAddProperty}
                      onRemoveProperty={handleRemoveProperty}
                      onToggleDynamic={handleToggleDynamic}
                      onToggleLineItem={handleToggleLineItem}
                      onRefreshProperties={handleRefreshProperties}
                      presets={presets}
                      onSavePreset={handleSavePreset}
                      onLoadPreset={handleLoadPreset}
                      onDeletePreset={handleDeletePreset}
                      fyStartMonth={settings.fiscalYearStartMonth ?? 7}
                      onQuickSetProbability={handleQuickSetProbability}
                    />
                  </section>

                  {/* ── Section 2: Reporting Overview ─────────────────────────── */}
                  <section data-testid="reporting-overview-section">
                    <div className="mb-4">
                      <h2 className="font-sans text-xl font-bold text-[#323c44]">Reporting Overview</h2>
                      <p className="mt-1 text-sm text-[#4a5b68]">Bespoke metrics configured in the metrics panel — these are independent of the filters above. <span className="text-[#7d8b96]">Use Configure metrics to add or edit.</span></p>
                    </div>
                    <KpiGrid
                      kpis={overviewKpis}
                      formatCurrency={formatCurrency}
                      visibleKpis={settings.visibleKpis}
                      customMetrics={settings.customMetrics}
                      fiscalYearStartMonth={settings.fiscalYearStartMonth ?? 7}
                      onConfigure={() => setMetricsConfigOpen(true)}
                      independentKpis={independentKpis}
                    />
                  </section>

                  {/* ── Section 2: ARR Report ──────────────────────────────────── */}
                  {report && report.products.length > 0 && (() => {
                    const palette6 = ["#50e3c2", "#3facc6", "#4a5b68", "#7d8b96", "#cccccc", "#a7f0df"];
                    const includeOneTime = appliedFilters.includeOneTime;
                    const productTotals = report.products.map((product, idx) => {
                      // product_values contains ONLY recurring MRR — the backend never
                      // writes one-time amounts into products, only into one_time_products.
                      // Do NOT subtract oneTime here; that would deflate recurring ARR.
                      const recurringArr = report.table.reduce((sum, row) => {
                        return sum + (row.product_values[product] || 0);
                      }, 0);
                      const oneTimeRevenue = report.table.reduce((sum, row) => {
                        return sum + ((row.one_time_product_values || {})[product] || 0);
                      }, 0);
                      return { product, recurringArr, oneTimeRevenue, color: palette6[idx % 6] };
                    });
                    // When grouping is on, keep the backend's canonical (Anna) order;
                    // otherwise rank the products by ARR descending.
                    if (!appliedFilters.collapseProducts) {
                      productTotals.sort((a, b) => b.recurringArr - a.recurringArr);
                    }
                    const totalRecurringArr = productTotals.reduce((sum, p) => sum + p.recurringArr, 0);
                    const totalOneTimeRevenue = productTotals.reduce((sum, p) => sum + p.oneTimeRevenue, 0);
                    const fromLabel = appliedFilters.fromMonth ? formatMonthLabel(appliedFilters.fromMonth) : "start";
                    const toLabel = appliedFilters.toMonth ? formatMonthLabel(appliedFilters.toMonth) : "present";
                    return (
                      <section data-testid="arr-report-section">
                        <div className="mb-4 flex items-start justify-between">
                          <div>
                            <h2 className="font-sans text-xl font-bold text-[#323c44]">{settings.arrTitle || "ARR Report"}</h2>
                            {settings.arrSubtitle && <p className="mt-1 text-sm text-[#4a5b68]">{settings.arrSubtitle}</p>}
                          </div>
                          <Button
                            variant="outline"
                            className="ml-4 h-9 shrink-0 rounded-md border border-[#dddddd] bg-white px-3 text-sm text-[#323c44] hover:bg-[#efefef]"
                            onClick={handleExportExcel}
                            data-testid="export-excel-button"
                          >
                            <Download className="mr-1 h-4 w-4" />
                            Generate Income Statement
                          </Button>
                          {/* One row per client per product, for reconciling
                              against the finance team's own schedule. */}
                          <Button
                            variant="outline"
                            className="ml-2 h-9 shrink-0 rounded-md border border-[#dddddd] bg-white px-3 text-sm text-[#323c44] hover:bg-[#efefef]"
                            onClick={handleExportClientRevenue}
                            data-testid="export-client-revenue-button"
                          >
                            <Download className="mr-1 h-4 w-4" />
                            Client revenue (CSV)
                          </Button>
                        </div>
                        <Card className="overflow-hidden border-[#dddddd] shadow-sm" data-testid="arr-report-card">
                          <CardContent className="p-0">
                            <table className="w-full text-sm" data-testid="arr-report-table">
                              <thead>
                                <tr className="border-b border-[#dddddd] bg-[#f5f5f5]">
                                  <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-[#4a5b68]">Product</th>
                                  <th className="px-6 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.18em] text-[#4a5b68]">ARR</th>
                                  {includeOneTime && <th className="px-6 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.18em] text-[#4a5b68]">One-time</th>}
                                  <th className="px-6 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.18em] text-[#4a5b68]">Share</th>
                                </tr>
                              </thead>
                              <tbody>
                                {productTotals.length === 0 && (
                                  <tr>
                                    <td colSpan={includeOneTime ? 4 : 3} className="px-6 py-12 text-center text-sm text-[#4a5b68]">
                                      No recurring revenue found for the selected filters.
                                    </td>
                                  </tr>
                                )}
                                {productTotals.map(({ product, recurringArr, oneTimeRevenue, color }) => {
                                  const isOpen = arrDrillProduct === product;
                                  const colSpan = includeOneTime ? 4 : 3;
                                  // Group drill deals by company
                                  const companiesMap = {};
                                  if (isOpen && arrDrillDeals) {
                                    for (const deal of arrDrillDeals) {
                                      const co = deal.company_name || "Unknown";
                                      if (!companiesMap[co]) companiesMap[co] = [];
                                      companiesMap[co].push(deal);
                                    }
                                  }
                                  const companyGroups = Object.entries(companiesMap).sort(
                                    ([, a], [, b]) => b.reduce((s, d) => s + d.total_arr, 0) - a.reduce((s, d) => s + d.total_arr, 0)
                                  );
                                  return (
                                    <>
                                      <tr
                                        key={product}
                                        className={`cursor-pointer border-b border-[#efefef] transition hover:bg-[#f5f5f5] ${isOpen ? "bg-[#f0f9ff]" : ""}`}
                                        onClick={() => handleArrProductClick(product)}
                                      >
                                        <td className="px-6 py-3 font-medium text-[#323c44]">
                                          <span className="mr-2 inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
                                          {product}
                                          <span className="ml-2 text-xs text-[#7d8b96]">{isOpen ? "▲" : "▼"}</span>
                                        </td>
                                        <td className="px-6 py-3 text-right tabular-nums font-mono text-[#323c44]">{formatCurrency(recurringArr)}</td>
                                        {includeOneTime && <td className="px-6 py-3 text-right tabular-nums font-mono text-[#7d8b96]">{oneTimeRevenue > 0 ? formatCurrency(oneTimeRevenue) : "—"}</td>}
                                        <td className="px-6 py-3 text-right tabular-nums text-[#4a5b68]">
                                          {totalRecurringArr > 0 ? `${((recurringArr / totalRecurringArr) * 100).toFixed(1)}%` : "—"}
                                        </td>
                                      </tr>
                                      {isOpen && (
                                        <tr key={`${product}-drill`} className="border-b border-[#dddddd] bg-[#f5f5f5]">
                                          <td colSpan={colSpan} className="px-6 py-4">
                                            {arrDrillLoading ? (
                                              <p className="text-sm text-[#4a5b68]">Loading deals…</p>
                                            ) : arrDrillDeals?.length === 0 ? (
                                              <p className="text-sm text-[#4a5b68]">No deals found for this product.</p>
                                            ) : (
                                              <table className="w-full text-xs">
                                                <thead>
                                                  <tr className="text-[#4a5b68]">
                                                    <th className="pb-2 text-left font-semibold">Deal</th>
                                                    <th className="pb-2 text-left font-semibold">Stage</th>
                                                    <th className="pb-2 text-left font-semibold">Owner</th>
                                                    <th className="pb-2 text-right font-semibold">Amount</th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {companyGroups.map(([company, deals]) => {
                                                    const companyTotal = deals.reduce((s, d) => s + d.total_arr, 0);
                                                    return (
                                                      <>
                                                        {/* Company header */}
                                                        <tr key={`co-${company}`} className="border-t border-[#dddddd] bg-[#efefef]">
                                                          <td colSpan={3} className="py-1.5 pr-4 font-semibold text-[#323c44]">{company}</td>
                                                          <td className="py-1.5 text-right tabular-nums font-mono font-semibold text-[#323c44]">{formatCurrency(companyTotal)}</td>
                                                        </tr>
                                                        {deals.map((deal) => {
                                                          const dealExpanded = arrExpandedDeals.has(deal.deal_id);
                                                          const visibleLineItems = (deal.line_items || []).filter(li => li.amount > 0);
                                                          return (
                                                            <>
                                                              {/* Deal row — click to expand/collapse line items */}
                                                              <tr
                                                                key={deal.deal_id}
                                                                className="cursor-pointer border-t border-[#efefef] bg-white hover:bg-[#f5f5f5]"
                                                                onClick={() => {
                                                                  setArrExpandedDeals(prev => {
                                                                    const next = new Set(prev);
                                                                    dealExpanded ? next.delete(deal.deal_id) : next.add(deal.deal_id);
                                                                    return next;
                                                                  });
                                                                }}
                                                              >
                                                                <td className="py-1.5 pl-4 pr-4 font-medium text-[#323c44]">
                                                                  <span className="mr-1.5 text-[#7d8b96] text-[10px]">{dealExpanded ? "▼" : "▶"}</span>
                                                                  {deal.deal_url ? (
                                                                    <a href={deal.deal_url} target="_blank" rel="noopener noreferrer" className="text-[#3facc6] hover:underline" onClick={e => e.stopPropagation()}>
                                                                      {deal.deal_name || deal.deal_id}
                                                                    </a>
                                                                  ) : (
                                                                    deal.deal_name || deal.deal_id
                                                                  )}
                                                                  {deal.is_one_time && (
                                                                    <span className="ml-2 rounded bg-[#fff7ed] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#ea580c]">One-time</span>
                                                                  )}
                                                                  {deal.one_time_revenue > 0 && !deal.is_one_time && (
                                                                    <span className="ml-2 rounded bg-[#fff7ed] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#ea580c]">Mixed</span>
                                                                  )}
                                                                </td>
                                                                <td className="py-1.5 pr-4 text-[#4a5b68]">{deal.deal_stage || "—"}</td>
                                                                <td className="py-1.5 pr-4 text-[#4a5b68]">{deal.owner || "—"}</td>
                                                                <td className="py-1.5 text-right tabular-nums font-mono font-semibold text-[#323c44]">{formatCurrency(deal.total_arr)}</td>
                                                              </tr>
                                                              {/* Line items — only shown when deal is expanded, £0 items hidden */}
                                                              {dealExpanded && visibleLineItems.map((li, liIdx) => (
                                                                <tr key={`${deal.deal_id}-li-${liIdx}`} className="border-t border-[#f5f5f5] bg-[#f5f5f5]">
                                                                  <td className="py-1 pl-10 pr-4 text-[#4a5b68]">
                                                                    <span className="mr-1.5 text-[#cccccc]">↳</span>
                                                                    {li.line_item_name}
                                                                    {li.is_one_time && (
                                                                      <span className="ml-2 rounded bg-[#fff7ed] px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-[#ea580c]">One-time</span>
                                                                    )}
                                                                  </td>
                                                                  <td className="py-1 pr-4 text-[#7d8b96]">{li.billing_frequency || "—"}</td>
                                                                  <td className="py-1 pr-4 text-[#7d8b96]">—</td>
                                                                  <td className="py-1 text-right tabular-nums font-mono text-[#4a5b68]">{formatCurrency(li.amount)}</td>
                                                                </tr>
                                                              ))}
                                                            </>
                                                          );
                                                        })}
                                                      </>
                                                    );
                                                  })}
                                                </tbody>
                                              </table>
                                            )}
                                          </td>
                                        </tr>
                                      )}
                                    </>
                                  );
                                })}
                              </tbody>
                              <tfoot>
                                <tr className="border-t-2 border-[#dddddd] bg-[#f5f5f5]">
                                  <td className="px-6 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#4a5b68]">ARR</td>
                                  <td className="px-6 py-3 text-right font-bold tabular-nums font-mono text-[#323c44]">{formatCurrency(totalRecurringArr)}</td>
                                  {includeOneTime && <td className="px-6 py-3 text-right tabular-nums font-mono text-[#7d8b96]">{formatCurrency(totalOneTimeRevenue)}</td>}
                                  <td className="px-6 py-3 text-right text-[#4a5b68]">100%</td>
                                </tr>
                                {includeOneTime && (
                                  <tr className="border-t border-[#dddddd] bg-[#fffbeb]">
                                    <td className="px-6 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#92400e]">ARR + One-time</td>
                                    <td className="px-6 py-3 text-right font-bold tabular-nums font-mono text-[#92400e]">{formatCurrency(totalRecurringArr + totalOneTimeRevenue)}</td>
                                    <td className="px-6 py-3 text-right tabular-nums font-mono text-[#92400e]">{formatCurrency(totalOneTimeRevenue)}</td>
                                    <td className="px-6 py-3" />
                                  </tr>
                                )}
                              </tfoot>
                            </table>
                          </CardContent>
                        </Card>
                      </section>
                    );
                  })()}

                  {/* ── Section 4: MRR ───────────────────────────────────────── */}
                  <section data-testid="mrr-reporting-section">
                    <div className="mb-4">
                      <h2 className="font-sans text-xl font-bold text-[#323c44]">MRR</h2>
                    </div>
                    <div className="space-y-4">
                      <Card className="overflow-hidden border-[#dddddd] shadow-sm" data-testid="report-surface-card">
                        <CardHeader className="border-b border-[#dddddd] bg-white px-6 py-5">
                          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                            <div>
                              <div className="inline-flex items-center gap-2 rounded-md bg-[#f5f5f5] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#4a5b68]" data-testid="report-surface-badge">
                                Monthly recurring revenue
                              </div>
                              <CardTitle className="mt-3 font-sans text-2xl text-[#323c44]" data-testid="report-surface-title">{settings.title}</CardTitle>
                              <CardDescription className="mt-2 max-w-3xl font-sans text-sm text-[#4a5b68]" data-testid="report-surface-subtitle">
                                {reportSubtitle}
                              </CardDescription>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {[
                                ["stacked", "Stacked"],
                                ["grouped", "Grouped"],
                                ["table", "Table"],
                              ].map(([mode, label]) => (
                                <Button
                                  key={mode}
                                  variant="outline"
                                  className={`h-10 rounded-full border px-3 ${viewMode === mode ? "border-[#57c1a9] bg-[#e8faf5] text-[#119996]" : "border-[#dddddd] bg-white text-[#323c44] hover:bg-[#efefef]"}`}
                                  onClick={() => setViewMode(mode)}
                                  data-testid={`view-mode-${mode}-button`}
                                >
                                  {label}
                                </Button>
                              ))}
                              <Button
                                variant="outline"
                                className="h-10 rounded-full border border-[#dddddd] bg-white px-3 text-[#323c44] hover:bg-[#efefef]"
                                onClick={handleViewAllLineItems}
                                data-testid="view-all-line-items-button"
                              >
                                <List className="mr-1 h-4 w-4" />
                                All line items
                              </Button>
                              <Button
                                variant="outline"
                                className="h-10 rounded-full border border-[#dddddd] bg-white px-3 text-[#323c44] hover:bg-[#efefef]"
                                onClick={handleExportCsv}
                                data-testid="export-csv-button"
                              >
                                <Download className="mr-1 h-4 w-4" />
                                Export MRR data
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="bg-white p-6">
                          {reportLoading ? (
                            <div className="rounded-md border border-[#dddddd] bg-[#f5f5f5] px-6 py-20 text-center text-sm text-[#4a5b68]" data-testid="report-loading-state">
                              Building the latest MRR view...
                            </div>
                          ) : report && report.products.length ? (
                            viewMode === "table" ? (
                              <ReportTable report={report} formatCurrency={formatCurrency} formatMonthLabel={formatMonthLabel} />
                            ) : (
                              <ReportChart
                                report={report}
                                settings={settings}
                                palette={palette}
                                viewMode={viewMode}
                                onMonthClick={handleMonthClick}
                                formatCurrency={formatCurrency}
                                formatMonthLabel={formatMonthLabel}
                              />
                            )
                          ) : (
                            <div className="rounded-md border border-dashed border-[#dddddd] bg-[#f5f5f5] px-6 py-20 text-center" data-testid="report-empty-state">
                              <p className="font-sans text-2xl text-[#323c44]">No recurring revenue rows yet.</p>
                              <p className="mt-3 font-sans text-sm text-[#4a5b68]">Connect a portal, run a sync, then return to reporting once recurring line items are available.</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </section>

                  {/* ── Section 5: Revenue by Client ───────────────────────────── */}
                  <section data-testid="revenue-by-client-section">
                    <div className="mb-4">
                      <h2 className="font-sans text-xl font-bold text-[#323c44]">{settings.revenueByClientTitle || "Revenue by Client"}</h2>
                      {settings.revenueByClientSubtitle && <p className="mt-1 text-sm text-[#4a5b68]">{settings.revenueByClientSubtitle}</p>}
                    </div>
                    <Card className="overflow-hidden border-[#dddddd] shadow-sm" data-testid="revenue-by-client-card">
                      <CardContent className="p-0">
                        {revenueLoading ? (
                          <div className="px-6 py-16 text-center text-sm text-[#4a5b68]">Loading client revenue…</div>
                        ) : revenueByClient?.clients?.length ? (
                          <div className="overflow-x-auto">
                            {(() => {
                              const showOt = appliedFilters.includeOneTime;
                              const totalArr = revenueByClient.clients.reduce((s, r) => s + r.arr, 0);
                              const totalOt = revenueByClient.clients.reduce((s, r) => s + (r.one_time_revenue || 0), 0);
                              return (
                            <table className="w-full text-sm" data-testid="revenue-by-client-table">
                              <thead>
                                <tr className="border-b border-[#dddddd] bg-[#f5f5f5]">
                                  <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-[#4a5b68]">Company</th>
                                  <th className="px-6 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.18em] text-[#4a5b68]">Avg MRR</th>
                                  <th className="px-6 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.18em] text-[#4a5b68]">ARR</th>
                                  {showOt && <th className="px-6 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.18em] text-[#4a5b68]">One-time</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {revenueByClient.clients.map((row, i) => (
                                  <tr
                                    key={row.company_name}
                                    className={`cursor-pointer border-b border-[#efefef] transition hover:bg-[#e8faf5] ${i % 2 === 0 ? "bg-white" : "bg-[#f5f5f5]"}`}
                                    onClick={() => handleCompanyClick(row.company_name)}
                                    data-testid={`revenue-client-row-${i}`}
                                  >
                                    <td className="px-6 py-3 font-medium text-[#323c44]">
                                      <span className="group-hover:text-[#119996]">{row.company_name}</span>
                                      <span className="ml-2 text-xs text-[#7d8b96] opacity-0 transition-opacity group-hover:opacity-100">↗</span>
                                    </td>
                                    <td className="px-6 py-3 text-right tabular-nums text-[#323c44]">{formatCurrency(row.mrr)}</td>
                                    <td className="px-6 py-3 text-right tabular-nums text-[#4a5b68]">{formatCurrency(row.arr)}</td>
                                    {showOt && <td className="px-6 py-3 text-right tabular-nums text-[#7d8b96]">{row.one_time_revenue > 0 ? formatCurrency(row.one_time_revenue) : "—"}</td>}
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="border-t-2 border-[#dddddd] bg-[#f5f5f5]">
                                  <td className="px-6 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#4a5b68]">
                                    Total ({revenueByClient.clients.length} clients)
                                  </td>
                                  <td className="px-6 py-3 text-right font-bold tabular-nums text-[#323c44]">
                                    {formatCurrency(revenueByClient.clients.reduce((s, r) => s + r.mrr, 0))}
                                  </td>
                                  <td className="px-6 py-3 text-right font-bold tabular-nums text-[#4a5b68]">
                                    {formatCurrency(totalArr)}
                                  </td>
                                  {showOt && <td className="px-6 py-3 text-right font-bold tabular-nums text-[#7d8b96]">{formatCurrency(totalOt)}</td>}
                                </tr>
                                {showOt && (
                                  <tr className="border-t border-[#dddddd] bg-[#fffbeb]">
                                    <td className="px-6 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#92400e]">ARR + One-time</td>
                                    <td className="px-6 py-3" />
                                    <td className="px-6 py-3 text-right font-bold tabular-nums text-[#92400e]">{formatCurrency(totalArr + totalOt)}</td>
                                    <td className="px-6 py-3 text-right font-bold tabular-nums text-[#92400e]">{formatCurrency(totalOt)}</td>
                                  </tr>
                                )}
                              </tfoot>
                            </table>
                              );
                            })()}
                          </div>
                        ) : (
                          <div className="px-6 py-16 text-center" data-testid="revenue-by-client-empty">
                            <p className="font-sans text-lg text-[#323c44]">No client revenue data yet.</p>
                            <p className="mt-2 text-sm text-[#4a5b68]">Apply filters above and ensure deals have associated companies with active recurring line items.</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </section>

                  {/* ── Churn Report ── */}
                  <section data-testid="churn-report-section-wrapper">
                    <div className="mb-4">
                      <h2 className="font-sans text-xl font-bold text-[#323c44]">Churn Report</h2>
                      <p className="mt-1 text-sm text-[#4a5b68]">Revenue at risk after renewal dates. Filters below are independent of the sections above.</p>
                    </div>

                    {/* Churn-specific filters */}
                    <FilterBar
                      filterOptions={filterOptions}
                      filters={churnDraftFilters}
                      onChange={handleChurnFilterChange}
                      onApply={handleApplyChurnFilters}
                      onReset={handleResetChurnFilters}
                      activePropertyKeys={churnActivePropertyKeys}
                      onAddProperty={handleAddChurnProperty}
                      onRemoveProperty={handleRemoveChurnProperty}
                      onToggleDynamic={handleToggleChurnDynamic}
                      onToggleLineItem={handleToggleChurnLineItem}
                      onRefreshProperties={handleRefreshProperties}
                      presets={presets}
                      onSavePreset={handleSaveChurnPreset}
                      onLoadPreset={handleLoadChurnPreset}
                      onDeletePreset={handleDeletePreset}
                      fyStartMonth={settings.fiscalYearStartMonth ?? 7}
                      onQuickSetProbability={handleQuickSetChurnProbability}
                    />

                    <ChurnReportSection
                      churnReport={churnReport}
                      loading={churnLoading}
                      formatCurrency={formatCurrency}
                      formatMonthLabel={formatMonthLabel}
                      palette={palette}
                    />
                  </section>

                </div>
              ) : (
                <Card className="border-dashed border-[#dddddd] bg-white shadow-sm" data-testid="no-portal-empty-state">
                  <CardContent className="px-6 py-16 text-center">
                    <p className="font-sans text-2xl text-[#323c44]">Connect a HubSpot portal first.</p>
                    <p className="mt-3 font-sans text-sm text-[#4a5b68]">Once connected, this page becomes the dedicated home for MRR filtering, charting, and month drill-downs.</p>
                  </CardContent>
                </Card>
              )}
            </SectionShell>
          }
        />
          </Routes>
        </div>
      </div>
      <DrilldownSheet open={drilldownOpen} onOpenChange={setDrilldownOpen} detail={monthDetail} loading={detailLoading} formatCurrency={formatCurrency} formatMonthLabel={formatMonthLabel} onRefetch={handleRefetchDetail} />
      <CompanyDrilldownSheet
        open={companyDrilldownOpen}
        onOpenChange={setCompanyDrilldownOpen}
        company={companyDrilldownCompany}
        // MRR is the month being viewed; ARR stays the report period.
        companyMrr={(companyDrilldownDetail?.records || [])
          .filter((r) => r.company_name === companyDrilldownCompany)
          .reduce((sum, r) => sum + (r.monthly_mrr || 0), 0)}
        companyArr={revenueByClient?.clients?.find((c) => c.company_name === companyDrilldownCompany)?.arr ?? 0}
        detail={companyDrilldownDetail}
        loading={companyDrilldownLoading}
        formatCurrency={formatCurrency}
        selectedMonth={companyDrilldownMonth}
        onMonthChange={handleCompanyMonthChange}
        lockToCompany={companyDrilldownLocked}
        onLockToCompanyChange={handleCompanyLockChange}
        appliedFiltersDescription={[
          ...(appliedFilters.pipelines?.length ? [`${appliedFilters.pipelines.join(", ")}`] : []),
          ...(appliedFilters.stages?.length ? [`${appliedFilters.stages.length} stage${appliedFilters.stages.length !== 1 ? "s" : ""}`] : []),
          ...(appliedFilters.owners?.length ? [appliedFilters.owners.join(", ")] : []),
        ].join(" · ") || "All deals"}
      />
      <MetricsConfigPanel
        open={metricsConfigOpen}
        onOpenChange={setMetricsConfigOpen}
        settings={settings}
        onUpdate={updateSetting}
        filterOptions={filterOptions}
      />
      <Toaster richColors position="top-right" />
    </div>
  );
}

export default function App() {
  const [authToken, setAuthToken] = useState(() => localStorage.getItem("auth_token"));
  const [authEmail, setAuthEmail] = useState(() => localStorage.getItem("auth_email"));

  const handleAuth = useCallback((token, email) => {
    setAuthToken(token);
    setAuthEmail(email);
  }, []);

  const handleLogout = useCallback(async () => {
    const API = process.env.REACT_APP_BACKEND_URL + "/api";
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${authToken}` },
      });
    } catch {
      /* best-effort */
    }
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_email");
    setAuthToken(null);
    setAuthEmail(null);
  }, [authToken]);

  // A reset link opens the reset form even for someone already signed in, so
  // the link never lands silently on the dashboard.
  const onResetLink = window.location.pathname.replace(/\/+$/, "") === "/reset-password";
  if (!authToken || onResetLink) {
    return <LoginPage onAuth={handleAuth} />;
  }

  return (
    <BrowserRouter>
      <AppShell authEmail={authEmail} onLogout={handleLogout} />
    </BrowserRouter>
  );
}
